import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { environment } from "src/environments/environment";

export interface PaginatedResponse {
  page: number;
  size: number;
  total: number;
  totalPages: number;
  filter: Filter;
  items: DistrictDirector[];
}

export interface Filter {
  excludeIds: number[];
  dateOfBirth: string | null;
  status: string | null;
  startDate: string | null;
  endDate: string | null;
  search: string | null;
}

export interface DistrictDirector {
  assigned: string;
  id: number;
  personMembership?: {
    id: number;
    personId: number;
    ratingSystemId: number;
    leagueSportId: number;
    person: {
      id: number,
      email: string;
      firstName: string;
      lastName: string;
      middleName: string;
      gender: "MALE" | "FEMALE",
      birthDate: string; //"1997-02-13",
      country: {
        id: number;
        name: string;
        code: string;
        codeAlpha3: string;
      },
      state: {
        id: number;
        name: string;
        code: string;
        countryId: number;
      },
      city: string;
      address: string;
      zipCode: string;
      phoneNumber: string;
    },
    amount: number;
    membershipSystem: string; // "AAU"
    membershipRegistrator: string; // "UVENTEX",
    membershipProgram: string; // "ADULT",
    status: string; //"APPROVED";
    startDate: string; // "2025-09-17",
    endDate: string; // "2026-08-31",
    createDate: string; // "2025-09-17 03:04:11",
    updateDate: string; // "2025-09-17 10:39:54",
    registrationDate: string; // "2025-09-17 03:04:11",
    membershipNumber: string; // "UJRQY4QNG",
    externalCode: string; // "21401",
    isNonAthlete: boolean;
    coverage: string; // "STANDARD",
    additionalText1: string; // "AAU_NON_ATHLETE_MEMBERSHIP",
    additionalText2: string; // "NCSI_CLEAR",
    countyName: string; // "ANNE ARUNDEL",
    districtCode: string; // "MD",
    districtName: string; // "Maryland",
  }
  memberNumber?: string;
  email?: string;
  sports?: { id: number, name: string }[];
  districts?: { id: number, name: string }[];

  fullName?: string;
}

export interface State {
  id: number;
  name: string;
  code: string;
  countryId: number;
}

export interface Country {
  id: number;
  name: string;
  code: string;
  codeAlpha3: string;
}

export interface CreatedBy {
  id: number;
  firstName: string;
  lastName: string;
  middleName: string;
  gender: string;
  birthDate: string;
  country: Country;
  state: State;
  province: string;
  city: string;
  address: string;
  zipCode: string;
  phoneNumber: string;
  sportTitle: string;
}


export interface DisciplineDTO {
  id?: number;
  caseNumber: string;
  status: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  gender: string;
  address: string;
  city: string;
  zipCode: string;
  dateOfBirth: string;
  memberNumber: string;
  workPhone?: string;
  homePhone: string;
  startDate: string;
  endDate: string;
  isEndDateIndefinite: boolean;
  state?: {
    id: number;
  };
  country: {
    id: number;
  };
  details: string;
}

interface FilterParams {
  page: number,
  size: number,
  filter: {
    excludeIds: [
      0
    ],
    search?: string;
    dateOfBirth?: string; //"2025-09-03T16:42:47.288Z",
    status?: "ACTIVE" | "BLOCK";
    startDate?: string; //"2025-09-03T16:42:47.288Z",
    endDate?: string; //"2025-09-03T16:42:47.288Z",
    sportId?: number;
    district?: string;
  },
  sort?: {
    orders: [
      {
        direction: "ASC" | "DESC",
        property: string,
      }
    ],
    empty: true
  },
  offset?: number
}

export interface LeaguePrivilege {
  privilege: "DISTRICT_DIRECTOR" | "LEAGUE_ADMIN";
  leagueId: number;
}

export enum LEAGUE_ID {
  AAU = 40,
  USAGR = 29974,
}



@Injectable({
  providedIn: "root",
})
export class AUUDistrictDirectorsService {
  accessAdminTools$: BehaviorSubject<LeaguePrivilege[] | "NONE" | "ADMIN"> = new BehaviorSubject<LeaguePrivilege[] | "NONE" | "ADMIN">("NONE");
  loadedPrivilege$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  selfDirector: DistrictDirector | undefined;
  baseUrl: string = environment.APIURL;
  filterParams: FilterParams = {
    page: 0,
    size: 10,
    filter: {
      excludeIds: [
        0
      ],
    },
  }


  privilege: "NONE" | "ADMIN" | LeaguePrivilege[] = "NONE";
  updatedPrivilege = false;

  constructor(private http: HttpClient) { }

  hasPrivilege(privilege: "DISTRICT_DIRECTOR" | "LEAGUE_ADMIN" | "ADMIN", leagueId: LEAGUE_ID): boolean {
    if (privilege === "ADMIN" && this.accessAdminTools$.value === "ADMIN") {
      return true;
    }
    return this.accessAdminTools$.value instanceof Array && this.accessAdminTools$.value.length > 0 && (this.accessAdminTools$.value.find((x: LeaguePrivilege) => x.privilege === privilege && x.leagueId === leagueId)) !== undefined;
  }


  filter(): Observable<PaginatedResponse> {
    const path = "api/event/integration/aau/admintools/district-director/filter";
    return this.http.post<PaginatedResponse>(`${this.baseUrl}${path}`, this.filterParams);
  }

  createDirector(body: { personMembership: { id: number; }; districts: { id: number; }[]; sports: { id: number; }[]; }) {
    const path = "api/event/integration/aau/admintools/district-director";
    return this.http.post(`${this.baseUrl}${path}`, body);
  }
  updateDirector(body: { id: number, personMembership: { id: number; }; districts: { id: number; }[]; sports: { id: number; }[]; }) {
    const path = `api/event/integration/aau/admintools/district-director`;
    return this.http.put(`${this.baseUrl}${path}`, body);
  }

  deleteDirector(id: number) {
    const path = `api/event/integration/aau/admintools/district-director/${id}`;
    return this.http.delete(`${this.baseUrl}${path}`);
  }

  selfDistrictDirector() {
    const path = `api/event/integration/aau/admintools/district-director/self`;
    return this.http.get(`${this.baseUrl}${path}`);
  }

  getDistrictDirectorPerson(id: number) {
    const path = `api/event/integration/aau/admintools/district-director/person/${id}`;
    return this.http.get(`${this.baseUrl}${path}`);
  }
}
